import React, { useState } from 'react';
import Image from 'next/image';

import HeadingBody from './heading-body';
import {
  Form,
  SelectFieldMinimal,
  TextFieldControlled as Input,
  Button,
  Logo,
} from '@leafwell/components';
import { useEventsTracker } from 'contexts/eventsTracker';

const DropperAmountList = [
  {
    label: '1 full dropper',
    value: '1',
  },
  {
    label: '3/4 dropper',
    value: '0.75',
  },
  {
    label: '1/2 dropper',
    value: '0.5',
  },
  {
    label: '1/4 dropper',
    value: '0.25',
  },
  {
    label: '2 full droppers',
    value: '2',
  },
  {
    label: '3 full droppers',
    value: '3',
  },
];

const ThcTinctureDoseForm: React.FC = () => {
  const { trackEvent } = useEventsTracker();

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dropperAmount, setDropperAmount] = useState<string>('');
  const [dose, setDose] = useState<number>(0);

  const onSubmit = data => {
    setIsLoading(true);

    const dose =
      (data.totalTincture / data.sizeOfBottle) *
      data.dropperSize *
      parseFloat(dropperAmount);
    setDose(Math.ceil(dose));
    setFormSubmitted(true);
    setIsLoading(false);
  };

  return (
    <>
      <Form
        className="grid gap-y-6"
        mode="onSubmit"
        defaultValues={{
          sizeOfBottle: '',
          totalTincture: '',
          dropperSize: '',
          dropperAmount: dropperAmount,
        }}
        onSubmit={onSubmit}
      >
        <div className="grid md:grid-cols-auto-1fr md:items-center gap-y-2 gap-x-6">
          <p className="font-medium md:min-w-60 xl:min-w-52">Size of bottle</p>
          <Input
            className="rounded-lg"
            name="sizeOfBottle"
            placeholder="In milliliters (mL)"
            type="number"
            min={1}
            max={99999}
            validations={['required', 'number']}
          />
        </div>
        <div className="grid md:grid-cols-auto-1fr md:items-center gap-y-2 gap-x-6">
          <p className="font-medium md:min-w-60 xl:min-w-52">
            Total THC content in bottle
          </p>
          <Input
            className="rounded-lg"
            name="totalTincture"
            placeholder="In milligrams (mg)"
            type="number"
            min={1}
            max={99999}
            validations={['required', 'number']}
          />
        </div>
        <div className="grid md:grid-cols-auto-1fr md:items-center gap-y-2 gap-x-6">
          <p className="font-medium md:min-w-60 xl:min-w-52">Dropper size</p>
          <Input
            className="rounded-lg"
            name="dropperSize"
            placeholder="In milliliters (mL)"
            type="number"
            min={1}
            max={99999}
            validations={['required', 'number']}
          />
        </div>
        <div className="grid md:grid-cols-auto-1fr md:items-center gap-y-2 gap-x-6">
          <p className="font-medium md:min-w-60 xl:min-w-52"> Dropper amount</p>
          <SelectFieldMinimal
            aria-label="Dropper amount"
            placeholder="Select amount"
            options={DropperAmountList}
            disabled={false}
            onSelect={target => {
              setDropperAmount(target.value);
            }}
          />
        </div>
        <Button
          disabled={false}
          type="submit"
          label="Calculate dose"
          variant="secondary"
          className="w-fit justify-self-center !text-lg rounded-lg font-medium font-sans"
          loading={isLoading}
          onClick={() => {
            trackEvent({
              event: 'select_content',
              content_type: 'Calculate dose',
              item_id: 'calculate_thc_tincture_dose',
              origin: window?.location?.pathname,
            });
          }}
        />
      </Form>
      {formSubmitted && dose && dose > 0 ? (
        <div className="relative w-full h-20 border border-purple-500 rounded flex items-center justify-center mt-6">
          <p className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 text-base xs:text-lg font-semibold text-purple-500 bg-[#EBECEB] p-2">
            Your THC dose
          </p>
          <p className="p-2 text-xl">Approximate dose: {dose} mg</p>
        </div>
      ) : null}
    </>
  );
};

const ThcTinctureDoseCalculator: React.FC = () => {
  return (
    <div className="grid gap-y-6 bg-[#EBECEB] rounded-[10px] p-8 not-prose mt-10 mb-6 mx-auto max-w-3xl">
      <Image
        quality={100}
        className="justify-self-center -mt-12 -mb-6"
        src="/thc-tincture-calc-top-icon.svg"
        alt="Thc Tincture Dose Calculator top icon"
        width={208}
        height={116}
      />
      <HeadingBody
        className="justify-self-center text-center font-medium text-purple-500"
        level="4"
        title="Calculate your THC tincture dose"
      />
      <ThcTinctureDoseForm />
      <p className="text-sm text-center">
        For educational purposes only. Not medical advice.{' '}
        <strong>
          Consult a healthcare provider before using cannabis products or making
          dosing decisions.
        </strong>{' '}
        Leafwell disclaims any liability arising from use of this tool.
      </p>
      <Logo className="justify-self-center w-36 md:mt-8" />
      <div className="-mx-8 -mb-8 pointer-events-none select-none">
        <Image
          src="/thc-tincture-calc-bottom-frame.svg"
          alt=""
          width={744}
          height={161}
          className="block w-full h-auto -mt-8"
          quality={100}
          priority={false}
          sizes="(min-width: 768px) 736px, 100vw"
        />
      </div>
    </div>
  );
};

export default ThcTinctureDoseCalculator;
